import { useEffect, useRef, useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { Calendar } from '@vnetwork/ui/primitives';
import styles from './DateRangePicker.module.css';

export interface DateRange {
  /** YYYY-MM-DD (date-only, no timezone). */
  from: string;
  to: string;
}

/** 'YYYY-MM-DD' → local Date (no TZ shift, unlike new Date('YYYY-MM-DD')). */
function parseDay(s: string): Date | null {
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}
function formatDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function display(s: string): string {
  const d = parseDay(s);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

/**
 * DS-native date-range picker: a Select-style trigger opening an in-flow
 * @vnetwork/ui Calendar (no portal — plays nicely inside Dialog scroll-lock,
 * same approach as service-select). First click picks the start date, second
 * click the end date; clicking before the start restarts the range.
 */
export function DateRangePicker({
  value,
  onChange,
  disabled,
  placeholder = 'Select date range',
}: {
  value: DateRange;
  onChange: (next: DateRange) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // While picking: a start date is staged until the end date confirms the range.
  const [draftFrom, setDraftFrom] = useState<Date | null>(null);

  // Close on outside click and Escape (house pattern — see service-select).
  useEffect(() => {
    if (!open) return;
    function onPointer(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pickingEnd = draftFrom !== null;
  const today = new Date();

  const pick = (d: Date) => {
    if (!pickingEnd) {
      setDraftFrom(d);
      return;
    }
    if (d < draftFrom) {
      // Earlier than the staged start — treat as a new start instead.
      setDraftFrom(d);
      return;
    }
    onChange({ from: formatDay(draftFrom), to: formatDay(d) });
    setDraftFrom(null);
    setOpen(false);
  };

  const label =
    value.from && value.to ? `${display(value.from)} – ${display(value.to)}` : placeholder;
  const selected = pickingEnd ? draftFrom : parseDay(value.from);

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        className={styles.trigger}
        data-placeholder={value.from && value.to ? undefined : ''}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => {
          setDraftFrom(null);
          setOpen((o) => !o);
        }}
      >
        <span className={styles.triggerLabel}>
          <CalendarDays size={15} className={styles.calendarIcon} />
          {label}
        </span>
      </button>

      {open && (
        <div className={styles.panel}>
          <div className={styles.hint} aria-live="polite">
            {pickingEnd ? `${display(formatDay(draftFrom))} → select end date` : 'Select start date'}
          </div>
          {/* No minDate while picking the end — clicking an earlier day restarts the range. */}
          <Calendar value={selected} onChange={pick} today={today} defaultMonth={selected ?? today} />
        </div>
      )}
    </div>
  );
}
